// oxlint-disable jsx-a11y/prefer-tag-over-role
import type React from 'react';

import { DEFAULT_ACCEPT, DEFAULT_TAB_INDEX, KEY_ENTER, KEY_SPACE } from './constants';
import { useDragAndDrop } from './use-drag-and-drop.hook';
import { useFileInput } from './use-file-input.hook';

import styles from './attachment-drag-and-drop.module.scss';

interface AttachmentDragAndDropProps {
    onFileSelect: (file: File) => void;
    text: string;
    accept?: string;
    multiple?: boolean;
    icon?: React.ReactNode;
    disabled?: boolean;
}

export const AttachmentDragAndDrop = ({
    onFileSelect,
    text,
    accept = DEFAULT_ACCEPT,
    multiple = false,
    icon,
    disabled = false,
}: AttachmentDragAndDropProps) => {
    const { fileInputRef, openFileDialog, handleFileInputChange, clearInputValue } = useFileInput({
        onFileSelect,
        multiple,
    });

    const { handleDrop, handleDragOver } = useDragAndDrop({
        onFileSelect,
        multiple,
    });

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === KEY_ENTER || e.key === KEY_SPACE) {
            e.preventDefault();
            openFileDialog();
        }
    };

    const containerClassName = disabled
        ? `${styles.attachment_drag_and_drop_container} ${styles.attachment_drag_and_drop_container_disabled}`
        : styles.attachment_drag_and_drop_container;

    return (
        <div
            className={containerClassName}
            onDrop={disabled ? undefined : handleDrop}
            onDragOver={disabled ? undefined : handleDragOver}
            onClick={disabled ? undefined : openFileDialog}
            onKeyDown={disabled ? undefined : handleKeyDown}
            role="button"
            tabIndex={disabled ? -1 : DEFAULT_TAB_INDEX}
            aria-label={text}
            aria-disabled={disabled}
            data-testid="attachment-drag-and-drop"
        >
            <input
                ref={fileInputRef}
                type="file"
                multiple={multiple}
                className={styles.attachment_drag_and_drop_input}
                onChange={handleFileInputChange}
                onClick={clearInputValue}
                accept={accept}
                disabled={disabled}
                aria-hidden="true"
                data-ot-attachment-upload-input
                data-testid="attachment-drag-and-drop-input"
            />

            {icon && <div className={styles.attachment_drag_and_drop_icon}>{icon}</div>}

            <span className={styles.attachment_drag_and_drop_text}>{text}</span>
        </div>
    );
};
